import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, Link2, Plus, Power, AlertTriangle, UserPlus } from 'lucide-react';
import { api } from '../../lib/api';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';

// In-house referral tracking dashboard. Links are minted here; signups that
// arrive via ?ref=<code> or /r/<code> get the code stamped on the user + org
// rows (see src/lib/referral.js), and this page aggregates them by code.
const APP_URL = 'https://app.medilink.vip';

const TYPE_LABELS = { law_firm: 'law firms', marketer: 'marketers', medical_practice: 'clinics' };

export default function ReferralLinks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New-link form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', label: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const [copied, setCopied] = useState(null); // code just copied
  const [assigning, setAssigning] = useState(null); // org id being re-attributed
  const [assignFilter, setAssignFilter] = useState('');

  const load = () => {
    setLoading(true);
    api.adminListReferrals()
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Aggregate attribution per code: orgs (split by type) + user accounts.
  const stats = useMemo(() => {
    const byCode = {};
    const bump = (code) => {
      if (!byCode[code]) byCode[code] = { orgs: [], users: 0, law: 0, med: 0, stalled: 0 };
      return byCode[code];
    };
    (data?.orgs || []).forEach(o => {
      const s = bump(o.referral_source);
      s.orgs.push(o);
      if (o.type === 'law_firm') s.law += 1;
      if (o.type === 'medical_practice') s.med += 1;
    });
    (data?.users || []).forEach(u => { const s = bump(u.referral_source); s.users += 1; if (u.stalled) s.stalled += 1; });
    return byCode;
  }, [data]);

  // Accounts that came through a link but never finished: no organization.
  const stalled = useMemo(
    () => (data?.users || []).filter(u => u.stalled).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [data],
  );
  const labelFor = (code) => (data?.links || []).find(l => l.code === code)?.label || code;
  const daysAgo = (iso) => { const d = Math.round((Date.now() - new Date(iso)) / 86400000); return d === 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`; };

  // Codes that produced signups but were never minted here (ad-hoc links).
  const unregistered = useMemo(() => {
    const known = new Set((data?.links || []).map(l => l.code));
    return Object.keys(stats).filter(c => !known.has(c)).sort();
  }, [data, stats]);

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${APP_URL}/r/${code}`).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(null), 1600);
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.adminCreateReferral(form);
      setForm({ code: '', label: '', notes: '' });
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (link) => {
    try {
      await api.adminUpdateReferral(link.id, { active: !link.active });
      load();
    } catch (err) { setError(err.message); }
  };

  // Hand-set attribution: a practice or firm that got in without a link (or
  // with the wrong one) is credited to a link here. Empty = no owner.
  const assignSource = async (org, code) => {
    setAssigning(org.id); setError('');
    try {
      await api.adminUpdateOrg(org.id, { referral_source: code || null });
      setData(d => {
        if (!d) return d;
        const rest = { ...org, referral_source: code || null };
        return {
          ...d,
          orgs: [...(d.orgs || []).filter(o => o.id !== org.id), ...(code ? [rest] : [])],
          unattributed: [...(d.unattributed || []).filter(o => o.id !== org.id), ...(code ? [] : [rest])]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
        };
      });
    } catch (err) { setError(err.message); }
    finally { setAssigning(null); }
  };

  const SourceSelect = ({ org }) => (
    <select
      className="px-2 py-1 bg-tertiary border border-strong rounded-[8px] text-[12px] text-primary-c font-[inherit] focus:outline-none focus:border-[#0DA7CA]"
      value={org.referral_source || ''}
      disabled={assigning === org.id}
      onChange={e => assignSource(org, e.target.value)}
    >
      <option value="">No link</option>
      {(data?.links || []).map(l => <option key={l.id} value={l.code}>{l.label} (/r/{l.code})</option>)}
      {org.referral_source && !(data?.links || []).some(l => l.code === org.referral_source) && (
        <option value={org.referral_source}>{org.referral_source} (unregistered)</option>
      )}
    </select>
  );

  const unattributed = useMemo(() => {
    const q = assignFilter.trim().toLowerCase();
    return (data?.unattributed || []).filter(o => !q || o.name.toLowerCase().includes(q) || (o.city || '').toLowerCase().includes(q));
  }, [data, assignFilter]);

  const inputClass = 'px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13,167,202,0.15)]';
  const thClass = 'text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3';

  return (
    <div className="flex-1 overflow-y-auto bg-primary">
      <div className="max-w-[1200px] mx-auto px-4 md:px-7 py-6">
        {/* Stacks on mobile so the button never wraps mid-label; row on sm+. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="min-w-0">
            <h1 className="text-[22px] font-bold text-primary-c">Referral links</h1>
            <p className="text-[13px] text-muted-c mt-0.5">
              Share <span className="font-mono">{APP_URL.replace('https://', '')}/r/&lt;code&gt;</span> — signups are attributed automatically.
            </p>
          </div>
          <button
            onClick={() => setShowForm(s => !s)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 gradient-accent text-white rounded-[10px] text-[13px] font-semibold shadow-glow hover:-translate-y-0.5 transition-all whitespace-nowrap shrink-0 self-start sm:self-auto"
          >
            <Plus size={14} strokeWidth={2.5} /> New link
          </button>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-[10px] bg-red-500/10 border border-red-500/30 text-[13px] text-red-500">{error}</div>
        )}

        {showForm && (
          <form onSubmit={handleCreate} className="bg-elevated rounded-xl border border-theme p-4 mb-4 flex flex-wrap gap-2.5 items-end">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-[11px] font-semibold text-muted-c uppercase tracking-wider mb-1.5">Code</label>
              <input
                className={inputClass + ' w-full'}
                placeholder="mike"
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                maxLength={40}
                required
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-[11px] font-semibold text-muted-c uppercase tracking-wider mb-1.5">Label</label>
              <input className={inputClass + ' w-full'} placeholder="Mike's network" value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))} required />
            </div>
            <div className="flex-[2] min-w-[220px]">
              <label className="block text-[11px] font-semibold text-muted-c uppercase tracking-wider mb-1.5">Notes (optional)</label>
              <input className={inputClass + ' w-full'} placeholder="Where this link is used" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <button type="submit" disabled={saving}
              className="px-4 py-[11px] gradient-accent text-white rounded-[10px] text-[13px] font-semibold disabled:opacity-60">
              {saving ? 'Creating…' : 'Create'}
            </button>
          </form>
        )}

        {loading ? (
          <LoadingSpinner size="lg" className="py-16" />
        ) : !(data?.links || []).length && !unregistered.length ? (
          <EmptyState
            title="No referral links yet"
            description="Mint your first code and share app.medilink.vip/r/<code> in emails, decks, or as a QR."
          />
        ) : (
          <>
            <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-theme">
                      <th className={thClass}>Link</th>
                      <th className={thClass + ' hidden md:table-cell'}>Signups</th>
                      <th className={thClass + ' hidden lg:table-cell'}>Accounts</th>
                      <th className={thClass + ' hidden md:table-cell'}>Stalled</th>
                      <th className={thClass + ' hidden lg:table-cell'}>Created</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.links || []).map(link => {
                      const s = stats[link.code] || { orgs: [], users: 0, law: 0, med: 0, stalled: 0 };
                      return (
                        <tr key={link.id} className="border-b border-theme last:border-0 hover:bg-tertiary/40 transition-colors">
                          <td className="px-5 py-3.5">
                            <div className="text-[13px] font-semibold text-primary-c">{link.label}</div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-[12px] font-mono text-accent-light">/r/{link.code}</span>
                              <button onClick={() => copyLink(link.code)} title="Copy full link"
                                className="text-muted-c hover:text-primary-c transition-colors">
                                {copied === link.code ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                              </button>
                            </div>
                            {link.notes && <div className="text-[11px] text-muted-c mt-0.5">{link.notes}</div>}
                          </td>
                          <td className="px-5 py-3.5 hidden md:table-cell">
                            <span className="text-[15px] font-bold text-primary-c">{s.orgs.length}</span>
                            {s.orgs.length > 0 && (
                              <span className="text-[11px] text-muted-c ml-1.5">
                                {[s.med ? `${s.med} ${TYPE_LABELS.medical_practice}` : null, s.law ? `${s.law} ${TYPE_LABELS.law_firm}` : null].filter(Boolean).join(' · ')}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 hidden lg:table-cell text-[13px] text-muted-c">{s.users}</td>
                          <td className="px-5 py-3.5 hidden md:table-cell">
                            {s.stalled > 0
                              ? <a href="#stalled" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500/10 text-amber-600"><AlertTriangle size={10} /> {s.stalled}</a>
                              : <span className="text-[12px] text-muted-c">—</span>}
                          </td>
                          <td className="px-5 py-3.5 hidden lg:table-cell text-[12px] text-muted-c">
                            {new Date(link.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${link.active ? 'bg-emerald-500/10 text-emerald-500' : 'bg-tertiary text-muted-c'}`}>
                              {link.active ? 'Active' : 'Paused'}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <button onClick={() => toggleActive(link)} title={link.active ? 'Pause link' : 'Reactivate link'}
                              className="text-muted-c hover:text-primary-c transition-colors">
                              <Power size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Attributed orgs, newest first — the actual receipts. */}
            {(data?.orgs || []).length > 0 && (
              <div className="mt-6">
                <h2 className="text-[15px] font-bold text-primary-c mb-3 flex items-center gap-2">
                  <Link2 size={15} className="text-accent-light" /> Attributed signups
                </h2>
                <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
                  {[...(data.orgs)].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(o => (
                    <div key={o.id}
                      className="flex items-center justify-between gap-3 px-5 py-3 border-b border-theme last:border-0 hover:bg-tertiary/40 transition-colors flex-wrap">
                      <Link to={`/admin/organizations/${o.id}`} className="min-w-0">
                        <span className="text-[13px] font-semibold text-primary-c hover:underline">{o.name}</span>
                        <span className="text-[11px] text-muted-c ml-2">{o.type === 'law_firm' ? 'Law firm' : o.type === 'marketer' ? 'Marketer' : 'Medical practice'}</span>
                      </Link>
                      <div className="flex items-center gap-3 shrink-0">
                        <SourceSelect org={o} />
                        <span className="text-[11px] text-muted-c">{new Date(o.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Got in without a link — give them an owner by hand. */}
            {(data?.unattributed || []).length > 0 && (
              <div className="mt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <div>
                    <h2 className="text-[15px] font-bold text-primary-c flex items-center gap-2">
                      <UserPlus size={15} className="text-accent-light" /> Not attributed ({(data?.unattributed || []).length})
                    </h2>
                    <p className="text-[12px] text-muted-c mt-0.5">Signed up without a referral link. Pick a link to credit the signup to its owner.</p>
                  </div>
                  <input className={inputClass + ' sm:w-[220px]'} placeholder="Filter by name or city" value={assignFilter}
                    onChange={e => setAssignFilter(e.target.value)} />
                </div>
                <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
                  {unattributed.length === 0 && <div className="px-5 py-3 text-[12px] text-muted-c">No matches.</div>}
                  {unattributed.map(o => (
                    <div key={o.id} className="flex items-center justify-between gap-3 px-5 py-3 border-b border-theme last:border-0 hover:bg-tertiary/40 transition-colors flex-wrap">
                      <Link to={`/admin/organizations/${o.id}`} className="min-w-0">
                        <span className="text-[13px] font-semibold text-primary-c hover:underline">{o.name}</span>
                        <span className="text-[11px] text-muted-c ml-2">{o.type === 'law_firm' ? 'Law firm' : 'Medical practice'}{o.city ? ` · ${o.city}${o.state ? `, ${o.state}` : ''}` : ''}</span>
                      </Link>
                      <div className="flex items-center gap-3 shrink-0">
                        <SourceSelect org={o} />
                        <span className="text-[11px] text-muted-c">{new Date(o.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stalled signups — came through a link, never finished. Who to chase. */}
            {stalled.length > 0 && (
              <div className="mt-6" id="stalled">
                <h2 className="text-[15px] font-bold text-primary-c mb-1 flex items-center gap-2">
                  <AlertTriangle size={15} className="text-amber-500" /> Stalled signups
                </h2>
                <p className="text-[12px] text-muted-c mb-3">Created an account through a link but never finished setting up a practice or firm. The stage says how far they got; the automated nudge goes out once at 24 hours — after that it is a phone call.</p>
                <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
                  {stalled.map(u => (
                    <Link key={u.id} to={`/admin/users/${u.id}`} className="flex items-start justify-between gap-4 px-5 py-3 border-b border-theme last:border-0 hover:bg-tertiary/40 transition-colors flex-wrap">
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-primary-c">
                          {[u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email}
                          {u.practice_name && <span className="text-[11.5px] text-muted-c font-normal ml-2">{u.practice_name}</span>}
                        </div>
                        <div className="text-[11.5px] text-muted-c">{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                        <div className="text-[11.5px] mt-1 flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-semibold">{u.signup_stage || 'Account only'}</span>
                          {u.nudged && <span className="text-muted-c">nudge email sent</span>}
                        </div>
                      </div>
                      <div className="text-right text-[11.5px] text-muted-c shrink-0">
                        <div><span className="font-mono text-accent-light">{u.referral_source}</span> · {labelFor(u.referral_source)}</div>
                        <div>signed up {daysAgo(u.created_at)}</div>
                        <div>{u.last_sign_in_at ? `last seen ${daysAgo(u.last_sign_in_at)}` : 'never signed back in'}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Ad-hoc codes that were never minted here — still counted. */}
            {unregistered.length > 0 && (
              <div className="mt-4 px-4 py-3 rounded-[10px] bg-amber-500/10 border border-amber-500/30 text-[12px] text-amber-600">
                Unregistered codes seen on signups: {unregistered.map(c => `${c} (${(stats[c]?.orgs.length || 0)} orgs, ${(stats[c]?.users || 0)} accounts)`).join(', ')}.
                Create a link with the same code to give it a label.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
